import { BuildDetailsPanels } from '@/features/builds/components/BuildDetailsPanels';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { Chip, Text, formatStatusLabel, resolveStatusChipTone } from '@/ui/primitives';
import type { BuildDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type BuildDetailContentProps = {
  build: BuildDto;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const BuildDetailContent = ({
  build,
  onDelete,
  deleteLoading = false,
}: BuildDetailContentProps): ReactElement => {
  const title = build.buildKey ?? build.id;
  const durationLabel =
    build.startedAt && build.finishedAt
      ? formatDuration(build.finishedAt.getTime() - build.startedAt.getTime())
      : null;

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Build" />
          {title}
        </Text>
      }
      status={
        <>
          {build.isLatest ? (
            <Chip label="Latest" tone="primary" transform="uppercase" size="small" />
          ) : null}
          <Chip
            label={formatStatusLabel(build.status)}
            tone={resolveStatusChipTone(build.status)}
            transform="uppercase"
            size="small"
          />
        </>
      }
      headerMeta={
        durationLabel ? (
          <Text role="bodyMuted" component="span" sx={{ whiteSpace: 'nowrap' }}>
            {durationLabel}
          </Text>
        ) : null
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Build"
          dialogDescription="This will permanently delete the build. This action cannot be undone."
          confirmLabel="Delete Build"
        />
      }
      metadata={<BuildDetailsPanels build={build} />}
    />
  );
};
